import { useEffect, useRef, useState, useCallback } from 'react'
import { useTimeTracking } from '@/hooks/use-time-tracking'
import { useTimerSettings } from '@/hooks/use-timer-settings'
import { Button } from '@/components/ui/button'
import { MonitorPlay } from 'lucide-react'

/**
 * FloatingTimer utiliza la API de Picture-in-Picture para mostrar un cronómetro flotante
 * que permanece visible incluso cuando el usuario cambia de pestaña o aplicación.
 */
export function FloatingTimer() {
    const { elapsedTime, activeEntry, togglePause, clockOut } = useTimeTracking()
    const { settings } = useTimerSettings()
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const [isPiPActive, setIsPiPActive] = useState(false)
    const animationFrameRef = useRef<number | null>(null)

    const formatTime = useCallback((seconds: number) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor((seconds % 3600) / 60)
        const s = seconds % 60
        if (settings.isPomodoroEnabled && h === 0 && seconds <= ((settings.pomodoroDuration || 25) * 60)) {
            return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
        }
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    }, [settings.isPomodoroEnabled, settings.pomodoroDuration])

    // 0. Sincronizar el estado del video con el estado de la app
    // Esto es CRUCIAL para que el widget de PiP muestre el icono correcto (Play vs Pause)
    useEffect(() => {
        if (!videoRef.current || !isPiPActive) return;

        if (activeEntry?.status === 'active') {
            videoRef.current.play().catch(() => { });
        } else {
            videoRef.current.pause();
        }
    }, [activeEntry?.status, isPiPActive]);

    // Forzar redibujado inmediato del canvas cuando cambia el estado
    // Esto proporciona feedback visual instantáneo al usuario
    useEffect(() => {
        if (!canvasRef.current || !isPiPActive) return;

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        // Trigger manual redraw para mostrar cambio de estado inmediatamente
        const canvas = ctx.canvas;
        const color = activeEntry?.status === 'paused' ? '245, 158, 11' : '163, 230, 21';

        // DEBUG: Verificar el estado actual

        // Fondo
        const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        bgGradient.addColorStop(0, '#18181b');
        bgGradient.addColorStop(1, '#09090b');
        ctx.fillStyle = bgGradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Glow
        const glow = ctx.createRadialGradient(
            canvas.width / 2, canvas.height / 2, 0,
            canvas.width / 2, canvas.height / 2, canvas.width / 1.5
        );
        glow.addColorStop(0, `rgba(${color}, 0.1)`);
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Borde
        ctx.strokeStyle = `rgba(${color}, 0.2)`;
        ctx.lineWidth = 4;
        ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);

        // Tiempo
        ctx.fillStyle = activeEntry?.status === 'paused' ? '#fbbf24' : '#a3e635';
        if (settings.isPomodoroEnabled && elapsedTime >= ((settings.pomodoroDuration || 25) * 60)) {
            ctx.fillStyle = '#ef4444'; // red-500
        }

        ctx.font = 'bold 58px "Courier New", Courier, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = `rgba(${color}, 0.4)`;
        ctx.shadowBlur = 15;

        let displaySecs = elapsedTime;
        let isOver = false;
        if (settings.isPomodoroEnabled) {
            const limit = (settings.pomodoroDuration || 25) * 60;
            if (elapsedTime > limit) {
                displaySecs = elapsedTime - limit;
                isOver = true;
            } else {
                displaySecs = limit - elapsedTime;
            }
        }

        ctx.fillText((isOver ? '+' : '') + formatTime(displaySecs), canvas.width / 2, canvas.height / 2 - 5);

        // Texto inferior
        ctx.shadowBlur = 0;
        ctx.font = 'bold 14px sans-serif';
        ctx.fillStyle = '#71717a';
        ctx.fillText(settings.isPomodoroEnabled ? 'TEMPORA • MODO POMODORO' : 'TEMPORA • SIGUIENDO TIEMPO', canvas.width / 2, canvas.height - 25);

        // Barra
        ctx.fillStyle = `rgba(${color}, 0.3)`;
        const barWidth = ((elapsedTime % 60) / 60) * (canvas.width - 40);
        ctx.fillRect(20, canvas.height - 10, barWidth, 2);

        // Estado superior
        if (activeEntry?.status === 'paused') {
            ctx.fillStyle = '#f59e0b';
            ctx.font = 'bold 12px sans-serif';
            ctx.fillText('SESIÓN PAUSADA', canvas.width / 2, 25);
        } else {
            ctx.fillStyle = '#a3e635';
            ctx.font = 'bold 12px sans-serif';
            ctx.fillText('EN VIVO', canvas.width / 2, 25);
        }
    }, [activeEntry?.status, isPiPActive, elapsedTime, formatTime, settings.isPomodoroEnabled, settings.pomodoroDuration]);

    // Configurar Media Session para permitir control desde el sistema (teclas multimedia, barra tareas)
    useEffect(() => {
        if ('mediaSession' in navigator && isPiPActive) {
            const isPaused = activeEntry?.status === 'paused';

            // Sincronizar el estado de reproducción con el sistema operativo
            navigator.mediaSession.playbackState = isPaused ? 'paused' : 'playing';

            navigator.mediaSession.metadata = new MediaMetadata({
                title: isPaused ? 'SESIÓN PAUSADA' : 'TEMPORA EN VIVO',
                artist: 'Sergio Quintero O.',
                album: activeEntry?.project?.name || 'Seguimiento de Tiempo',
                artwork: [
                    { src: 'https://img.icons8.com/flat-round/512/time-machine.png', sizes: '512x512', type: 'image/png' }
                ]
            });

            // Handler para REANUDAR (Botón Play del sistema)
            navigator.mediaSession.setActionHandler('play', () => {
                if (activeEntry?.status === 'paused') togglePause();
            });

            // Handler para PAUSAR (Botón Pause del sistema)
            navigator.mediaSession.setActionHandler('pause', () => {
                if (activeEntry?.status === 'active') togglePause();
            });

            // Handler para DETENER
            navigator.mediaSession.setActionHandler('stop', () => {
                if (window.confirm("¿Deseas detener y guardar la sesión?")) {
                    clockOut();
                }
            });

            // ELIMINAR handlers de navegación para quitar flechas innecesarias (YouTube style)
            const actionsToRemove = ['previoustrack', 'nexttrack', 'seekbackward', 'seekforward', 'seekto', 'skipad'];
            actionsToRemove.forEach(action => {
                try {
                    navigator.mediaSession.setActionHandler(action as MediaSessionAction, null);
                } catch {
                    // Ignorar errores de acciones no soportadas
                }
            });
        }
    }, [isPiPActive, activeEntry?.status, activeEntry?.project?.name, togglePause, clockOut]);

    // Función de dibujado extraída para reutilización
    // IMPORTANTE: Usar useCallback para que se actualice cuando cambie el estado
    const drawFrame = useCallback((ctx: CanvasRenderingContext2D) => {
        const canvas = ctx.canvas;

        // 1. Limpiar y Fondo con Gradiente
        const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height)
        bgGradient.addColorStop(0, '#18181b') // zinc-900
        bgGradient.addColorStop(1, '#09090b') // zinc-950
        ctx.fillStyle = bgGradient
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // 2. Efecto de Brillo/Luz (Glow)
        const glow = ctx.createRadialGradient(
            canvas.width / 2, canvas.height / 2, 0,
            canvas.width / 2, canvas.height / 2, canvas.width / 1.5
        )
        const color = activeEntry?.status === 'paused' ? '245, 158, 11' : '163, 230, 21' // amber vs lime
        glow.addColorStop(0, `rgba(${color}, 0.1)`)
        glow.addColorStop(1, 'transparent')
        ctx.fillStyle = glow
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        // 3. Dibujar Borde Interior (Diseño más redondeado visualmente)
        ctx.strokeStyle = `rgba(${color}, 0.2)`
        ctx.lineWidth = 4
        ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8)

        // 4. Texto del Cronómetro Principal
        ctx.fillStyle = activeEntry?.status === 'paused' ? '#fbbf24' : '#a3e635'
        if (settings.isPomodoroEnabled && elapsedTime >= ((settings.pomodoroDuration || 25) * 60)) {
            ctx.fillStyle = '#ef4444'; // red-500
        }
        ctx.font = 'bold 58px "Courier New", Courier, monospace'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'

        // Sombra de neón
        ctx.shadowColor = `rgba(${color}, 0.4)`
        ctx.shadowBlur = 15

        let displaySecs = elapsedTime;
        let isOver = false;
        if (settings.isPomodoroEnabled) {
            const limit = (settings.pomodoroDuration || 25) * 60;
            if (elapsedTime > limit) {
                displaySecs = elapsedTime - limit;
                isOver = true;
            } else {
                displaySecs = limit - elapsedTime;
            }
        }

        ctx.fillText((isOver ? '+' : '') + formatTime(displaySecs), canvas.width / 2, canvas.height / 2 - 5)

        // 5. Detalles Inferiores
        ctx.shadowBlur = 0
        ctx.font = 'bold 14px sans-serif'
        ctx.fillStyle = '#71717a' // zinc-400
        ctx.fillText(settings.isPomodoroEnabled ? 'TEMPORA • MODO POMODORO' : 'TEMPORA • SIGUIENDO TIEMPO', canvas.width / 2, canvas.height - 25)

        // 6. Barra de Progreso Sutil (Simulada o decorativa)
        ctx.fillStyle = `rgba(${color}, 0.3)`
        const barWidth = ((elapsedTime % 60) / 60) * (canvas.width - 40)
        ctx.fillRect(20, canvas.height - 10, barWidth, 2)

        // 7. Indicador de Estado Superior
        if (activeEntry?.status === 'paused') {
            ctx.fillStyle = '#f59e0b'
            ctx.font = 'bold 12px sans-serif'
            ctx.fillText('SESIÓN PAUSADA', canvas.width / 2, 25)
        } else {
            ctx.fillStyle = '#a3e635'
            ctx.font = 'bold 12px sans-serif'
            ctx.fillText('EN VIVO', canvas.width / 2, 25)
        }
    }, [activeEntry?.status, elapsedTime, formatTime, settings.isPomodoroEnabled, settings.pomodoroDuration])

    // Efecto para dibujar el tiempo en el canvas continuamente
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const draw = () => {
            drawFrame(ctx);
            if (isPiPActive) {
                animationFrameRef.current = requestAnimationFrame(draw)
            }
        }

        if (isPiPActive) {
            animationFrameRef.current = requestAnimationFrame(draw)
            return () => {
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current)
                }
            }
        } else {
            drawFrame(ctx) // Dibujar una vez para el estado inicial
        }
    }, [elapsedTime, activeEntry, isPiPActive, drawFrame])

    const togglePiP = async () => {
        const video = videoRef.current
        const canvas = canvasRef.current

        if (!video || !canvas) return

        try {
            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture()
            } else {
                // Preparamos el stream solo si no existe
                if (!video.srcObject) {
                    const canvasElement = canvas as HTMLCanvasElement & { captureStream(frameRate?: number): MediaStream };
                    const stream = canvasElement.captureStream(30)
                    video.srcObject = stream
                }

                // Pequeño delay para asegurar que el stream está listo en algunos navegadores
                await new Promise(resolve => setTimeout(resolve, 100))

                // Intentar reproducir y luego entrar en PiP
                await video.play()
                await video.requestPictureInPicture()
                setIsPiPActive(true)

                video.addEventListener('leavepictureinpicture', () => {
                    setIsPiPActive(false)
                }, { once: true })
            }
        } catch (error) {
            console.error('Picture-in-Picture failed:', error)
        }
    }

    if (!activeEntry) return null

    return (
        <div className="flex items-center">
            {/* Elementos invisibles pero presentes en el DOM (Display: none suele romper el PiP) */}
            <canvas ref={canvasRef} width={400} height={200} className="fixed pointer-events-none opacity-0 -z-50" />
            <video
                ref={videoRef}
                className="fixed pointer-events-none opacity-0 -z-50"
                muted
                playsInline
                onPause={() => {
                    if (isPiPActive && activeEntry?.status === 'active') {
                        togglePause();
                    }
                }}
                onPlay={() => {
                    if (isPiPActive && activeEntry?.status === 'paused') {
                        togglePause();
                    }
                }}
            />

            <Button
                variant="outline"
                size="sm"
                onClick={togglePiP}
                className={`h-9 px-2 md:px-3 gap-2 border-border shadow-sm transition-all duration-300 focus-visible:ring-2 focus-visible:ring-primary ${isPiPActive ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90' : 'bg-background hover:bg-accent'}`}
                title="Reloj flotante (PiP)"
                aria-label={isPiPActive ? "Cerrar reloj flotante" : "Abrir reloj flotante (Picture-in-Picture)"}
                aria-pressed={isPiPActive}
            >
                <MonitorPlay className={`w-4 h-4 ${isPiPActive ? 'animate-pulse' : ''}`} />
                <span className="text-[11px] font-bold uppercase tracking-tight hidden md:inline">Reloj PiP</span>
            </Button>
        </div>
    )
}
